import { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  TextField, MenuItem, Select, InputLabel, FormControl, Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Users, UserCheck, UserX, Clock } from 'lucide-react';
import { attendanceApi } from '@/api/attendance.api';
import { departmentsApi } from '@/api/departments.api';
import { designationsApi } from '@/api/designations.api';
import useSchoolStore from '@/store/schoolStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useNavigate } from 'react-router-dom';

export function DailyAttendancePage() {
  usePageMeta('Daily Attendance | Attendance', [
    { label: 'Attendance', path: '/attendance' },
    { label: 'Daily Overview' },
  ]);

  const { currentSchool } = useSchoolStore();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);

  // DataGrid state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // Filter state
  const [filters, setFilters] = useState({
    attendance_date: new Date().toISOString().split('T')[0],
    search: '',
    department_id: '',
    designation_id: '',
    attendance_status: '',
  });

  // Dropdown options
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  useEffect(() => {
    if (currentSchool?.id) {
      fetchDropdowns();
    }
  }, [currentSchool]);

  useEffect(() => {
    if (currentSchool?.id) {
      fetchSummary();
      fetchLogs();
    }
  }, [currentSchool, paginationModel, filters]);

  const fetchDropdowns = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        departmentsApi.getAll(currentSchool.id),
        designationsApi.getAll(currentSchool.id)
      ]);
      // Extract arrays if the API wraps them in { departments: [...] }, { designations: [...] }, { items: [...] }, or { data: [...] }
      const deptArray = deptRes?.departments || deptRes?.items || deptRes?.data || (Array.isArray(deptRes) ? deptRes : []);
      const desigArray = desigRes?.designations || desigRes?.items || desigRes?.data || (Array.isArray(desigRes) ? desigRes : []);

      setDepartments(deptArray);
      setDesignations(desigArray);
    } catch (err) {
      console.error("Failed to load dropdowns", err);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await attendanceApi.getTodaySummary(currentSchool.id);
      if (res.success) {
        setSummary(res.summary);
      }
    } catch (error) {
      console.error('Failed to fetch attendance summary', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        attendance_date: filters.attendance_date,
      };

      if (filters.search) params.search = filters.search;
      if (filters.department_id) params.department_id = filters.department_id;
      if (filters.designation_id) params.designation_id = filters.designation_id;
      if (filters.attendance_status) params.attendance_status = filters.attendance_status;

      const res = await attendanceApi.getAttendanceLogs(currentSchool.id, params);
      if (res.success) {
        setRows(res.items || []);
        setRowCount(res.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch attendance logs', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset page on filter change
  };

  const handleRowClick = (params) => {
    const staffId = params.row.staff?.id;
    if (staffId) {
      navigate(`/attendance/staff/${staffId}?date=${filters.attendance_date}`);
    }
  };

  const columns = [
    {
      field: 'employeeName',
      headerName: 'Employee',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight="500">{params.row.staff?.name}</Typography>
          <Typography variant="caption" color="text.secondary">{params.row.staff?.employee_code}</Typography>
        </Box>
      )
    },
    {
      field: 'department',
      headerName: 'Department',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => params.row.department?.name || '--'
    },
    {
      field: 'designation',
      headerName: 'Designation',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => params.row.designation?.name || '--'
    },
    {
      field: 'checkIn',
      headerName: 'Check In',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => params.row.check_in_time ? new Date(params.row.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'
    },
    {
      field: 'checkOut',
      headerName: 'Check Out',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => params.row.check_out_time ? new Date(params.row.check_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const status = params.row.attendance_status;
        let color = 'default';
        if (status === 'PRESENT') color = 'success';
        if (status === 'ABSENT') color = 'error';
        if (status === 'HALF_DAY') color = 'warning';
        if (status === 'ON_LEAVE') color = 'info';
        return <Chip size="small" label={status || 'UNKNOWN'} color={color} sx={{ fontWeight: 600, fontSize: '0.75rem' }} />;
      }
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Daily Attendance Overview"
        subtitle={`Summary for ${new Date(filters.attendance_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Total Staff</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>{summary?.total_staff || 0}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.lighter', color: 'primary.main' }}>
                <Users size={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Present</Typography>
                <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 1 }}>{summary?.present || 0}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.lighter', color: 'success.main' }}>
                <UserCheck size={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Absent</Typography>
                <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 1 }}>{summary?.absent || 0}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'error.lighter', color: 'error.main' }}>
                <UserX size={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Late</Typography>
                <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 1 }}>{summary?.late || 0}</Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.lighter', color: 'warning.main' }}>
                <Clock size={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Attendance Date"
                value={filters.attendance_date}
                onChange={(e) => handleFilterChange('attendance_date', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Search Staff"
                placeholder="Name or Code..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={filters.department_id}
                  label="Department"
                  onChange={(e) => handleFilterChange('department_id', e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Designation</InputLabel>
                <Select
                  value={filters.designation_id}
                  label="Designation"
                  onChange={(e) => handleFilterChange('designation_id', e.target.value)}
                >
                  <MenuItem value="">All Designations</MenuItem>
                  {designations.map((desig) => (
                    <MenuItem key={desig.id} value={desig.id}>{desig.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.attendance_status}
                  label="Status"
                  onChange={(e) => handleFilterChange('attendance_status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="PRESENT">PRESENT</MenuItem>
                  <MenuItem value="ABSENT">ABSENT</MenuItem>
                  <MenuItem value="HALF_DAY">HALF_DAY</MenuItem>
                  <MenuItem value="ON_LEAVE">ON_LEAVE</MenuItem>
                  <MenuItem value="HOLIDAY">HOLIDAY</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          getRowId={(row) => row.attendance_id || Math.random().toString()}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={{
            border: 0,
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
        />
      </Paper>
    </Box>
  );
}
